import { Sparkles, PhoneCall, Radio, Mic, CheckCircle, PhoneMissed } from 'lucide-react';

// Visualises a call's lifecycle so the user can see exactly what's happening
// from the moment they hit "call" through connection and completion.
const CALL_PHASES = [
  { key: 'prepare', label: 'Preparing', icon: Sparkles },
  { key: 'placing', label: 'Placing call', icon: PhoneCall },
  { key: 'ringing', label: 'Ringing', icon: Radio },
  { key: 'live', label: 'Connected', icon: Mic },
  { key: 'ended', label: 'Ended', icon: CheckCircle },
];

const CALL_STEP_HINT: Record<string, string> = {
  prepare: 'Loading account & contact context and the agent script…',
  placing: 'Handing the call to the provider — dialing out now…',
  ringing: 'Ringing the contact’s phone…',
  live: 'Contact answered — AI agent is on the call. Live transcript below.',
  ended: 'Call finished — fetching the transcript & summary.',
};

export function phaseIndex(status?: string, initiating?: boolean): number {
  if (initiating && !status) return 0;
  switch (status) {
    case 'queued': case 'initiated': case 'registered': return 1;
    case 'ringing': return 2;
    case 'in-progress': case 'ongoing': return 3;
    case 'ended': case 'completed': case 'failed': case 'no-answer': case 'busy': case 'error': return 4;
    default: return initiating ? 0 : 1;
  }
}

export default function CallProgressStepper({ status, initiating }: { status?: string; initiating?: boolean }) {
  const active = phaseIndex(status, initiating);
  const failed = ['failed', 'no-answer', 'busy', 'error'].includes(status || '');
  return (
    <div>
      <div className="flex items-start">
        {CALL_PHASES.map((p, i) => {
          const done = i < active;
          const isActive = i === active;
          const Icon = failed && isActive ? PhoneMissed : p.icon;
          return (
            <div key={p.key} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-1 shrink-0">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 transition-colors
                  ${done ? 'bg-primary border-primary text-primary-foreground'
                    : isActive ? (failed ? 'border-red-500 text-red-500' : 'border-primary text-primary animate-pulse')
                    : 'border-border text-muted-foreground'}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className={`text-[10px] text-center ${isActive ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                  {failed && isActive ? 'Failed' : p.label}
                </span>
              </div>
              {i < CALL_PHASES.length - 1 && (
                <div className={`h-0.5 flex-1 mx-1 mt-[-16px] ${done ? 'bg-primary' : 'bg-border'}`} />
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground text-center mt-2">
        {failed ? 'The call didn’t connect — check the number and provider config.' : CALL_STEP_HINT[CALL_PHASES[active]?.key] || ''}
      </p>
    </div>
  );
}
